import React, { useState, useEffect } from "react";
import { Col, Row, Card, Button, Table, Spinner, Dropdown, ButtonGroup, Form, Alert, Modal } from '@themesberg/react-bootstrap';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCaretDown, faCheck, faTimes } from "@fortawesome/free-solid-svg-icons";
import { client } from "../../api/client";

const API_BASE = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1' ? window.location.origin : 'https://sgrloto.com';

export default () => {
  const [agents, setAgents] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState("list-active"); // list-active, list-inactive, profile, add-agent
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");

  // Add Agent Form states
  const [addAgentForm, setAddAgentForm] = useState({
    branch: "",
    device_id: "",
    address: "",
    last_name: "",
    first_name: "",
    phone_number: "",
    username: "",
    password: "",
    commission_rate: "0",
    pct_sup: "0",
    credit_limit: "Libre",
    balance_limit: "Libre"
  });
  const [addAgentError, setAddAgentError] = useState("");
  const [savingAgent, setSavingAgent] = useState(false);

  // Blocked numbers list for profile view
  const [blockedNumbers, setBlockedNumbers] = useState([]);
  const [newBlockedNumber, setNewBlockedNumber] = useState("");
  const [blockAlert, setBlockAlert] = useState(false);
  const [disabledDraws, setDisabledDraws] = useState([]);
  const [loadingAgentDetails, setLoadingAgentDetails] = useState(false);

  // Edit Agent states
  const [showEditModal, setShowEditModal] = useState(false);
  const [editForm, setEditForm] = useState({
    username: "",
    first_name: "",
    last_name: "",
    device_id: "",
    branch: "",
    commission_rate: "",
    supervisor_commission_rate: ""
  });
  const [editError, setEditError] = useState("");
  const [savingEdit, setSavingEdit] = useState(false);

  // Primes config for profile view
  const [primeMode, setPrimeMode] = useState("general"); // general, par-tirage
  const [draws, setDraws] = useState([]);
  const [selectedDrawId, setSelectedDrawId] = useState("");
  const [agentGrilles, setAgentGrilles] = useState([]);
  const [loadingAgentPrimes, setLoadingAgentPrimes] = useState(false);

  // Form states for custom agent prime overrides
  const [b1, setB1] = useState("");
  const [b2, setB2] = useState("");
  const [b3, setB3] = useState("");
  const [l3, setL3] = useState("");
  const [mar, setMar] = useState("");
  const [l4_1, setL4_1] = useState("");
  const [l4_2, setL4_2] = useState("");
  const [l4_3, setL4_3] = useState("");
  const [l5_1, setL5_1] = useState("");
  const [l5_2, setL5_2] = useState("");
  const [l5_3, setL5_3] = useState("");
  const [currentGrilleId, setCurrentGrilleId] = useState(null);

  const fetchAgents = async () => {
    setLoading(true);
    try {
      const data = await client("agents/");
      const list = data ? (data.results || (Array.isArray(data) ? data : [])) : [];
      const apiAgents = list.map((a) => ({
        id: a.id,
        username: a.username,
        first_name: a.first_name || "",
        last_name: a.last_name || "",
        device_id: a.device_id || "n/a",
        branch: a.branch || a.branch_id || "",
        branch_name: a.branch_name || "Principale",
        bank_name: a.bank_name || a.bank_pdv_name || "Lotto Bank",
        supervisor_name: a.supervisor_name || "n/a",
        commission_rate: a.commission_rate || 10,
        is_active: a.is_active,
        prime: a.prime || "60|20|10",
        pct_sup: a.supervisor_commission_rate || "0",
        message: "0"
      }));

      setAgents(apiAgents);
    } catch (err) {
      console.error("Agents fetch error:", err);
      setAgents([]);
    }
    setLoading(false);
  };

  const fetchDraws = async () => {
    try {
      const data = await client("reports/filters/");
      if (data && data.draws) {
        setDraws(data.draws);
        if (data.draws.length > 0) {
          setSelectedDrawId(data.draws[0].id);
        }
      }
    } catch (e) {
      console.error("Failed to fetch draws:", e);
    }
  };

  const fetchAgentPrimes = async (agentId) => {
    if (!agentId || String(agentId).startsWith("mock-")) {
      setAgentGrilles([]);
      return;
    }
    setLoadingAgentPrimes(true);
    try {
      const data = await client(`grille-gains/?target_level=AGENT&agent=${agentId}`);
      const list = data ? (data.results || (Array.isArray(data) ? data : [])) : [];
      setAgentGrilles(list);
    } catch (e) {
      console.error("Failed to fetch agent grilles:", e);
    }
    setLoadingAgentPrimes(false);
  };

  const fetchBranches = async () => {
    try {
      const data = await client("branches/");
      const list = data ? (data.results || (Array.isArray(data) ? data : [])) : [];
      setBranches(list);
    } catch (e) {
      console.error("Failed to fetch branches:", e);
    }
  };

  useEffect(() => {
    fetchAgents();
    fetchDraws();
    fetchBranches();
  }, []);

  const fetchDisabledDraws = async (agentId) => {
    try {
      const data = await client(`agents/disabled-draws/?agent=${agentId}`);
      setDisabledDraws(data || []);
    } catch (e) {
      console.error("Failed to fetch disabled draws:", e);
    }
  };

  const fetchBlockedNumbers = async (agentId) => {
    try {
      const data = await client(`risk/number-overrides/?target_level=AGENT`);
      const list = data ? (data.results || (Array.isArray(data) ? data : [])) : [];
      const agentList = list.filter(o => o.agent === agentId && o.is_blocked);
      setBlockedNumbers(agentList);
    } catch (e) {
      console.error("Failed to fetch blocked numbers:", e);
    }
  };

  useEffect(() => {
    if (selectedAgent) {
      fetchAgentPrimes(selectedAgent.id);
      fetchDisabledDraws(selectedAgent.id);
      fetchBlockedNumbers(selectedAgent.id);
    }
  }, [selectedAgent]);

  useEffect(() => {
    if (!selectedAgent) return;
    
    // Find current grille
    let found = null;
    if (primeMode === "general") {
      found = agentGrilles.find(g => !g.draw);
    } else {
      found = agentGrilles.find(g => g.draw === parseInt(selectedDrawId) || g.draw === selectedDrawId);
    }

    if (found) {
      setCurrentGrilleId(found.id);
      setB1(found.borlette_win1 ?? "");
      setB2(found.borlette_win2 ?? "");
      setB3(found.borlette_win3 ?? "");
      setL3(found.loto3_multiplier ?? "");
      setMar(found.mariage_multiplier ?? "");
      setL4_1(found.loto4_opt1 ?? "");
      setL4_2(found.loto4_opt2 ?? "");
      setL4_3(found.loto4_opt3 ?? "");
      setL5_1(found.loto5_opt1 ?? "");
      setL5_2(found.loto5_opt2 ?? "");
      setL5_3(found.loto5_opt3 ?? "");
    } else {
      setCurrentGrilleId(null);
      // Fallback/company defaults:
      setB1("60");
      setB2("20");
      setB3("10");
      setL3("500");
      setMar("1000");
      setL4_1("5000");
      setL4_2("5000");
      setL4_3("5000");
      setL5_1("25000");
      setL5_2("25000");
      setL5_3("25000");
    }
  }, [agentGrilles, primeMode, selectedDrawId, selectedAgent]);

  const handleSavePrime = async () => {
    if (!selectedAgent || String(selectedAgent.id).startsWith("mock-")) {
      alert("Impossible de modifier la prime d'un agent de démonstration/fictif.");
      return;
    }

    const payload = {
      target_level: "AGENT",
      agent: selectedAgent.id,
      borlette_win1: parseInt(b1) || 0,
      borlette_win2: parseInt(b2) || 0,
      borlette_win3: parseInt(b3) || 0,
      loto3_multiplier: parseInt(l3) || 0,
      mariage_multiplier: parseInt(mar) || 0,
      loto4_opt1: parseInt(l4_1) || 0,
      loto4_opt2: parseInt(l4_2) || 0,
      loto4_opt3: parseInt(l4_3) || 0,
      loto5_opt1: parseInt(l5_1) || 0,
      loto5_opt2: parseInt(l5_2) || 0,
      loto5_opt3: parseInt(l5_3) || 0
    };

    if (primeMode === "par-tirage") {
      payload.draw = parseInt(selectedDrawId) || selectedDrawId;
    }

    try {
      if (currentGrilleId) {
        await client(`grille-gains/${currentGrilleId}/`, {
          method: "PUT",
          body: payload
        });
      } else {
        await client(`grille-gains/`, {
          method: "POST",
          body: payload
        });
      }
      alert("Configuration de la prime enregistrée avec succès !");
      fetchAgentPrimes(selectedAgent.id);
      fetchAgents();
    } catch (e) {
      console.error("Save error:", e);
      alert("Erreur lors de l'enregistrement de la prime.");
    }
  };

  const handleResetPrime = async () => {
    if (!currentGrilleId) {
      alert("Aucune surcharge spécifique à réinitialiser pour cet agent.");
      return;
    }
    if (!window.confirm("Voulez-vous réinitialiser cette prime ? L'agent héritera à nouveau de la prime par défaut de la compagnie.")) {
      return;
    }
    try {
      await client(`grille-gains/${currentGrilleId}/`, {
        method: "DELETE"
      });
      alert("Prime réinitialisée avec succès !");
      fetchAgentPrimes(selectedAgent.id);
      fetchAgents();
    } catch (e) {
      console.error("Reset error:", e);
    }
  };

  const handleToggleStatus = async (agent) => {
    // Toggle state locally first
    const updatedAgents = agents.map(a => 
      a.id === agent.id ? { ...a, is_active: !a.is_active } : a
    );
    setAgents(updatedAgents);

    try {
      await client(`agents/${agent.id}/`, {
        method: "PUT",
        body: { is_active: !agent.is_active }
      });
    } catch (e) {
      console.error("Failed to update status on server:", e);
    }
  };

  const handleAddBlocked = async () => {
    const num = newBlockedNumber.trim();
    if (!num) return;

    // Détection automatique du type de jeu par la longueur/format du numéro
    let gameType = "BORLETTE";
    if (num.includes("-")) {
      gameType = "MARIAGE";
    } else if (num.length === 3) {
      gameType = "LOTO3";
    } else if (num.length === 4) {
      gameType = "LOTO4";
    } else if (num.length === 5) {
      gameType = "LOTO5";
    }

    try {
      await client(`risk/number-overrides/`, {
        method: "POST",
        body: {
          target_level: "AGENT",
          agent: selectedAgent.id,
          number: num,
          game_type: gameType,
          limit_amount: "0",
          is_blocked: true,
          is_active: true
        }
      });
      setNewBlockedNumber("");
      setBlockAlert(true);
      setTimeout(() => setBlockAlert(false), 3000);
      fetchBlockedNumbers(selectedAgent.id);
    } catch (e) {
      console.error("Failed to add blocked number:", e);
      alert(e.error || e.detail || e.message || "Erreur lors du blocage du numéro.");
    }
  };

  const handleRemoveBlocked = async (overrideId) => {
    if (!window.confirm("Voulez-vous débloquer ce numéro pour cet agent ?")) return;
    try {
      await client(`risk/number-overrides/${overrideId}/`, {
        method: "DELETE"
      });
      fetchBlockedNumbers(selectedAgent.id);
    } catch (e) {
      console.error("Failed to delete blocked number:", e);
    }
  };

  const handleDisableDraw = async (drawId) => {
    try {
      await client(`agents/disabled-draws/`, {
        method: "POST",
        body: {
          agent: selectedAgent.id,
          draw: drawId
        }
      });
      fetchDisabledDraws(selectedAgent.id);
    } catch (e) {
      console.error("Failed to disable draw:", e);
    }
  };

  const handleEnableDraw = async (disabledDrawId) => {
    try {
      await client(`agents/disabled-draws/${disabledDrawId}/`, {
        method: "DELETE"
      });
      fetchDisabledDraws(selectedAgent.id);
    } catch (e) {
      console.error("Failed to enable draw:", e);
    }
  };

  const handleOpenEdit = () => {
    setEditForm({
      username: selectedAgent.username || "",
      first_name: selectedAgent.first_name || "",
      last_name: selectedAgent.last_name || "",
      device_id: selectedAgent.device_id || "",
      branch: (selectedAgent.branch && typeof selectedAgent.branch === 'object') ? (selectedAgent.branch.id || "") : (selectedAgent.branch || selectedAgent.branch_id || ""),
      commission_rate: selectedAgent.commission_rate || "0",
      supervisor_commission_rate: selectedAgent.pct_sup || "0"
    });
    setEditError("");
    setShowEditModal(true);
  };

  const handleSaveEdit = async (e) => {
    e.preventDefault();
    setEditError("");
    setSavingEdit(true);
    try {
      const data = await client(`agents/${selectedAgent.id}/`, {
        method: "PUT",
        body: {
          username: editForm.username,
          first_name: editForm.first_name,
          last_name: editForm.last_name,
          device_id: editForm.device_id,
          branch: editForm.branch || null,
          commission_rate: parseFloat(editForm.commission_rate) || 0,
          supervisor_commission_rate: parseFloat(editForm.supervisor_commission_rate) || 0
        }
      });

      // Mettre à jour l'agent sélectionné localement
      const selectedBranchObj = branches.find(b => String(b.id) === String(editForm.branch));
      const updatedAgent = {
        ...selectedAgent,
        username: data.username,
        first_name: data.first_name,
        last_name: data.last_name,
        device_id: data.device_id,
        branch: data.branch || editForm.branch || "",
        branch_name: data.branch_name || (selectedBranchObj ? selectedBranchObj.name : (editForm.branch ? selectedAgent.branch_name : "Non assigné")),
        commission_rate: data.commission_rate,
        pct_sup: data.supervisor_commission_rate
      };
      setSelectedAgent(updatedAgent);
      
      // Mettre à jour dans la liste des agents
      setAgents(agents.map(a => a.id === selectedAgent.id ? updatedAgent : a));
      
      alert("Informations de l'agent mises à jour avec succès !");
      setShowEditModal(false);
    } catch (err) {
      console.error("Edit agent error:", err);
      setEditError(err.error || err.detail || err.message || "Erreur lors de la mise à jour.");
    } finally {
      setSavingEdit(false);
    }
  };

  const handleCreateAgent = async (e) => {
    e.preventDefault();
    setAddAgentError("");

    // Validate required fields
    const required = ["branch", "device_id", "address", "last_name", "first_name", "phone_number", "username", "password"];
    for (const key of required) {
      if (!addAgentForm[key] || String(addAgentForm[key]).trim() === "") {
        setAddAgentError("Veuillez remplir tous les champs obligatoires.");
        return;
      }
    }

    setSavingAgent(true);
    try {
      const payload = {
        branch: addAgentForm.branch,
        device_id: addAgentForm.device_id,
        address: addAgentForm.address,
        last_name: addAgentForm.last_name,
        first_name: addAgentForm.first_name,
        phone_number: addAgentForm.phone_number,
        username: addAgentForm.username,
        password: addAgentForm.password,
        commission_rate: parseFloat(addAgentForm.commission_rate) || 0,
        supervisor_commission_rate: parseFloat(addAgentForm.pct_sup) || 0,
      };

      await client("agents/", {
        method: "POST",
        body: payload
      });

      alert("Agent créé avec succès !");
      
      // Reset form
      setAddAgentForm({
        branch: "",
        device_id: "",
        address: "",
        last_name: "",
        first_name: "",
        phone_number: "",
        username: "",
        password: "",
        commission_rate: "0",
        pct_sup: "0",
        credit_limit: "Libre",
        balance_limit: "Libre"
      });

      // Reload lists and back to list view
      fetchAgents();
      setViewMode("list-active");
    } catch (err) {
      console.error("Error creating agent:", err);
      setAddAgentError(err.error || err.detail || err.message || "Erreur lors de la création de l'agent.");
    } finally {
      setSavingAgent(false);
    }
  };

  const getFilteredAgents = () => {
    const wantActive = viewMode === "list-active";
    return agents.filter(a => {
      const matchesSearch = 
        a.username.toLowerCase().includes(searchQuery.toLowerCase()) ||
        a.first_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        a.device_id.toLowerCase().includes(searchQuery.toLowerCase());
      return matchesSearch && (a.is_active === wantActive);
    });
  };

  const filtered = getFilteredAgents();

  return (
    <div style={{ padding: '16px 0' }}>
      {/* Header Tabs matching exact style */}
      <div className="d-flex mb-4" style={{ gap: '10px' }}>
        <Button 
          style={{
            backgroundColor: '#007BFF',
            borderColor: '#007BFF',
            color: '#FFF',
            borderRadius: '4px',
            fontSize: '0.9rem',
            padding: '6px 20px',
            fontWeight: '500'
          }}
          onClick={() => { setViewMode("add-agent"); setSelectedAgent(null); }}
        >
          Ajouter
        </Button>
        <Button 
          variant={viewMode === "list-active" ? "success" : "outline-success"}
          style={{
            borderRadius: '4px',
            fontSize: '0.9rem',
            padding: '6px 20px',
            fontWeight: '500',
            color: viewMode === "list-active" ? '#FFF' : undefined
          }}
          onClick={() => { setViewMode("list-active"); setSelectedAgent(null); }}
        >
          Lister Actif
        </Button>
        <Button 
          variant={viewMode === "list-inactive" ? "warning" : "outline-warning"}
          style={{
            borderRadius: '4px',
            fontSize: '0.9rem',
            padding: '6px 20px',
            fontWeight: '500',
            color: viewMode === "list-inactive" ? '#222' : undefined
          }}
          onClick={() => { setViewMode("list-inactive"); setSelectedAgent(null); }}
        >
          Lister Inactif
        </Button>
      </div>

      {loading ? (
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
          <div className="text-muted small mt-2">Chargement des agents...</div>
        </div>
      ) : viewMode === "add-agent" ? (
        /* Add Agent Form View */
        <Card className="border-0 shadow-sm" style={{ borderRadius: '8px', padding: '24px' }}>
          {addAgentError && (
            <Alert variant="danger" className="py-2 px-3 small mb-3">
              {addAgentError}
            </Alert>
          )}

          <Form onSubmit={handleCreateAgent}>
            <Row className="mb-3">
              {/* Succursal (Groupe) */}
              <Col xs={12} md={4} className="mb-2">
                <Form.Label className="fw-bold small mb-1" style={{ color: '#333' }}>Succursal (Groupe)</Form.Label>
                <div className="position-relative d-flex align-items-center">
                  <Form.Select
                    style={{
                      borderColor: addAgentForm.branch ? '#28A745' : '#DC3545',
                      paddingRight: '36px',
                      borderRadius: '4px',
                      height: '38px'
                    }}
                    value={addAgentForm.branch}
                    onChange={(e) => setAddAgentForm({ ...addAgentForm, branch: e.target.value })}
                  >
                    <option value="">Choisir Succursal</option>
                    {branches.map(b => (
                      <option key={b.id} value={b.id}>{b.name}</option>
                    ))}
                  </Form.Select>
                  <FontAwesomeIcon 
                    icon={addAgentForm.branch ? faCheck : faTimes} 
                    className={addAgentForm.branch ? "text-success" : "text-danger"} 
                    style={{ position: 'absolute', right: '24px', pointerEvents: 'none' }} 
                  />
                </div>
              </Col>

              {/* DEVICE ID */}
              <Col xs={12} md={4} className="mb-2">
                <Form.Label className="fw-bold small mb-1" style={{ color: '#333' }}>DEVICE ID</Form.Label>
                <div className="position-relative d-flex align-items-center">
                  <Form.Control
                    type="text"
                    style={{
                      borderColor: addAgentForm.device_id.trim() ? '#28A745' : '#DC3545',
                      paddingRight: '36px',
                      borderRadius: '4px',
                      height: '38px'
                    }}
                    value={addAgentForm.device_id}
                    onChange={(e) => setAddAgentForm({ ...addAgentForm, device_id: e.target.value })}
                  />
                  <FontAwesomeIcon 
                    icon={addAgentForm.device_id.trim() ? faCheck : faTimes} 
                    className={addAgentForm.device_id.trim() ? "text-success" : "text-danger"} 
                    style={{ position: 'absolute', right: '12px', pointerEvents: 'none' }} 
                  />
                </div>
              </Col>

              {/* Zone / Addresse */}
              <Col xs={12} md={4} className="mb-2">
                <Form.Label className="fw-bold small mb-1" style={{ color: '#333' }}>Zone / Addresse</Form.Label>
                <div className="position-relative d-flex align-items-center">
                  <Form.Control
                    type="text"
                    style={{
                      borderColor: addAgentForm.address.trim() ? '#28A745' : '#DC3545',
                      paddingRight: '36px',
                      borderRadius: '4px',
                      height: '38px'
                    }}
                    value={addAgentForm.address}
                    onChange={(e) => setAddAgentForm({ ...addAgentForm, address: e.target.value })}
                  />
                  <FontAwesomeIcon 
                    icon={addAgentForm.address.trim() ? faCheck : faTimes} 
                    className={addAgentForm.address.trim() ? "text-success" : "text-danger"} 
                    style={{ position: 'absolute', right: '12px', pointerEvents: 'none' }} 
                  />
                </div>
              </Col>
            </Row>

            <Row className="mb-3">
              {/* Nom agent */}
              <Col xs={12} md={4} className="mb-2">
                <Form.Label className="fw-bold small mb-1" style={{ color: '#333' }}>Nom agent</Form.Label>
                <div className="position-relative d-flex align-items-center">
                  <Form.Control
                    type="text"
                    style={{
                      borderColor: addAgentForm.last_name.trim() ? '#28A745' : '#DC3545',
                      paddingRight: '36px',
                      borderRadius: '4px',
                      height: '38px'
                    }}
                    value={addAgentForm.last_name}
                    onChange={(e) => setAddAgentForm({ ...addAgentForm, last_name: e.target.value })}
                  />
                  <FontAwesomeIcon 
                    icon={addAgentForm.last_name.trim() ? faCheck : faTimes} 
                    className={addAgentForm.last_name.trim() ? "text-success" : "text-danger"} 
                    style={{ position: 'absolute', right: '12px', pointerEvents: 'none' }} 
                  />
                </div>
              </Col>

              {/* Prénom agent */}
              <Col xs={12} md={4} className="mb-2">
                <Form.Label className="fw-bold small mb-1" style={{ color: '#333' }}>Prénom agent</Form.Label>
                <div className="position-relative d-flex align-items-center">
                  <Form.Control
                    type="text"
                    style={{
                      borderColor: addAgentForm.first_name.trim() ? '#28A745' : '#DC3545',
                      paddingRight: '36px',
                      borderRadius: '4px',
                      height: '38px'
                    }}
                    value={addAgentForm.first_name}
                    onChange={(e) => setAddAgentForm({ ...addAgentForm, first_name: e.target.value })}
                  />
                  <FontAwesomeIcon 
                    icon={addAgentForm.first_name.trim() ? faCheck : faTimes} 
                    className={addAgentForm.first_name.trim() ? "text-success" : "text-danger"} 
                    style={{ position: 'absolute', right: '12px', pointerEvents: 'none' }} 
                  />
                </div>
              </Col>

              {/* Téléphone */}
              <Col xs={12} md={4} className="mb-2">
                <Form.Label className="fw-bold small mb-1" style={{ color: '#333' }}>Téléphone</Form.Label>
                <div className="position-relative d-flex align-items-center">
                  <Form.Control
                    type="text"
                    style={{
                      borderColor: addAgentForm.phone_number.trim() ? '#28A745' : '#DC3545',
                      paddingRight: '36px',
                      borderRadius: '4px',
                      height: '38px'
                    }}
                    value={addAgentForm.phone_number}
                    onChange={(e) => setAddAgentForm({ ...addAgentForm, phone_number: e.target.value })}
                  />
                  <FontAwesomeIcon 
                    icon={addAgentForm.phone_number.trim() ? faCheck : faTimes} 
                    className={addAgentForm.phone_number.trim() ? "text-success" : "text-danger"} 
                    style={{ position: 'absolute', right: '12px', pointerEvents: 'none' }} 
                  />
                </div>
              </Col>
            </Row>

            <Row className="mb-3">
              {/* Identifiant */}
              <Col xs={12} md={6} className="mb-2">
                <Form.Label className="fw-bold small mb-1" style={{ color: '#333' }}>Identifiant</Form.Label>
                <div className="position-relative d-flex align-items-center">
                  <Form.Control
                    type="text"
                    style={{
                      borderColor: addAgentForm.username.trim() ? '#28A745' : '#DC3545',
                      paddingRight: '36px',
                      borderRadius: '4px',
                      height: '38px'
                    }}
                    value={addAgentForm.username}
                    onChange={(e) => setAddAgentForm({ ...addAgentForm, username: e.target.value })}
                  />
                  <FontAwesomeIcon 
                    icon={addAgentForm.username.trim() ? faCheck : faTimes} 
                    className={addAgentForm.username.trim() ? "text-success" : "text-danger"} 
                    style={{ position: 'absolute', right: '12px', pointerEvents: 'none' }} 
                  />
                </div>
              </Col>

              {/* Mot de passe */}
              <Col xs={12} md={6} className="mb-2">
                <Form.Label className="fw-bold small mb-1" style={{ color: '#333' }}>Mot de passe</Form.Label>
                <div className="position-relative d-flex align-items-center">
                  <Form.Control
                    type="password"
                    style={{
                      borderColor: addAgentForm.password.trim() ? '#28A745' : '#DC3545',
                      paddingRight: '36px',
                      borderRadius: '4px',
                      height: '38px'
                    }}
                    value={addAgentForm.password}
                    onChange={(e) => setAddAgentForm({ ...addAgentForm, password: e.target.value })}
                  />
                  <FontAwesomeIcon 
                    icon={addAgentForm.password.trim() ? faCheck : faTimes} 
                    className={addAgentForm.password.trim() ? "text-success" : "text-danger"} 
                    style={{ position: 'absolute', right: '12px', pointerEvents: 'none' }} 
                  />
                </div>
              </Col>
            </Row>

            <Row className="mb-3">
              {/* %Agent */}
              <Col xs={12} md={6} className="mb-2">
                <Form.Label className="fw-bold small mb-1" style={{ color: '#333' }}>%Agent</Form.Label>
                <div className="position-relative d-flex align-items-center">
                  <Form.Control
                    type="text"
                    style={{
                      borderColor: '#28A745',
                      paddingRight: '36px',
                      borderRadius: '4px',
                      height: '38px'
                    }}
                    value={addAgentForm.commission_rate}
                    onChange={(e) => setAddAgentForm({ ...addAgentForm, commission_rate: e.target.value })}
                  />
                  <FontAwesomeIcon 
                    icon={faCheck} 
                    className="text-success" 
                    style={{ position: 'absolute', right: '12px', pointerEvents: 'none' }} 
                  />
                </div>
              </Col>

              {/* %Superviseur */}
              <Col xs={12} md={6} className="mb-2">
                <Form.Label className="fw-bold small mb-1" style={{ color: '#333' }}>%Superviseur</Form.Label>
                <div className="position-relative d-flex align-items-center">
                  <Form.Control
                    type="text"
                    style={{
                      borderColor: '#28A745',
                      paddingRight: '36px',
                      borderRadius: '4px',
                      height: '38px'
                    }}
                    value={addAgentForm.pct_sup}
                    onChange={(e) => setAddAgentForm({ ...addAgentForm, pct_sup: e.target.value })}
                  />
                  <FontAwesomeIcon 
                    icon={faCheck} 
                    className="text-success" 
                    style={{ position: 'absolute', right: '12px', pointerEvents: 'none' }} 
                  />
                </div>
              </Col>
            </Row>

            <Row className="mb-4">
              {/* Limite crédit */}
              <Col xs={12} md={6} className="mb-2">
                <Form.Label className="fw-bold small mb-1" style={{ color: '#333' }}>Limite crédit</Form.Label>
                <div className="position-relative d-flex align-items-center">
                  <Form.Control
                    type="text"
                    style={{
                      borderColor: '#28A745',
                      paddingRight: '36px',
                      borderRadius: '4px',
                      height: '38px'
                    }}
                    value={addAgentForm.credit_limit}
                    onChange={(e) => setAddAgentForm({ ...addAgentForm, credit_limit: e.target.value })}
                  />
                  <FontAwesomeIcon 
                    icon={faCheck} 
                    className="text-success" 
                    style={{ position: 'absolute', right: '12px', pointerEvents: 'none' }} 
                  />
                </div>
              </Col>

              {/* Limite balance gain */}
              <Col xs={12} md={6} className="mb-2">
                <Form.Label className="fw-bold small mb-1" style={{ color: '#333' }}>Limite balance gain</Form.Label>
                <div className="position-relative d-flex align-items-center">
                  <Form.Control
                    type="text"
                    style={{
                      borderColor: '#28A745',
                      paddingRight: '36px',
                      borderRadius: '4px',
                      height: '38px'
                    }}
                    value={addAgentForm.balance_limit}
                    onChange={(e) => setAddAgentForm({ ...addAgentForm, balance_limit: e.target.value })}
                  />
                  <FontAwesomeIcon 
                    icon={faCheck} 
                    className="text-success" 
                    style={{ position: 'absolute', right: '12px', pointerEvents: 'none' }} 
                  />
                </div>
              </Col>
            </Row>

            <Button
              type="submit"
              className="w-100 py-2 fw-bold text-white mb-2"
              style={{
                backgroundColor: '#007BFF',
                borderColor: '#007BFF',
                borderRadius: '4px',
                fontSize: '1rem'
              }}
              disabled={savingAgent}
            >
              {savingAgent ? <Spinner animation="border" size="sm" /> : "Enregistrer"}
            </Button>
            
            <Button
              variant="outline-secondary"
              className="w-100 py-2 fw-bold"
              style={{
                borderRadius: '4px',
                fontSize: '1rem'
              }}
              onClick={() => setViewMode("list-active")}
            >
              Annuler
            </Button>
          </Form>
        </Card>
      ) : viewMode.startsWith("list") ? (
        /* Agent List View */
        <Card className="border-0 shadow-sm" style={{ borderRadius: '8px' }}>
          <Card.Header className="bg-white border-0 py-3">
            <Row className="align-items-center">
              <Col>
                <h4 style={{ fontWeight: 'bold', color: viewMode === "list-active" ? '#28A745' : '#FFC107', margin: 0, textTransform: 'uppercase' }}>
                  {viewMode === "list-active" ? "POS ACTIF" : "POS INACTIF"}
                </h4>
              </Col>
            </Row>
          </Card.Header>

          {/* Action buttons + Search */}
          <div className="px-3 pb-3 d-flex justify-content-between align-items-center flex-wrap" style={{ gap: '10px' }}>
            <div className="d-flex" style={{ gap: '4px' }}>
              {["COPIER", "EXCEL", "PDF", "IMPRIMER"].map((label, idx) => (
                <button
                  key={idx}
                  style={{
                    backgroundColor: '#F5F5F5',
                    border: '1px solid #CCCCCC',
                    color: '#333333',
                    fontSize: '0.78rem',
                    padding: '6px 16px',
                    borderRadius: '2px',
                    height: '34px',
                    fontWeight: 'normal',
                    outline: 'none'
                  }}
                  onClick={() => alert(`${label} cliqué`)}
                >
                  {label}
                </button>
              ))}
            </div>

            <div className="d-flex align-items-center" style={{ gap: '8px' }}>
              <span className="fw-bold small text-dark" style={{ fontSize: '0.92rem' }}>Search:</span>
              <Form.Control 
                type="text" 
                size="sm" 
                style={{ width: '220px', borderRadius: '4px', border: '1px solid #CCC' }} 
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </div>

          {/* Table with grid borders matching screenshot */}
          <div className="table-responsive" style={{ padding: '0 16px 16px 16px' }}>
            <Table bordered hover className="align-items-center mb-0" style={{ borderCollapse: 'collapse', borderColor: '#DDD' }}>
              <thead>
                <tr style={{ backgroundColor: '#F8F9FA' }}>
                  <th style={{ width: '90px', padding: '12px 8px', textAlign: 'center' }}></th>
                  <th style={{ fontSize: '0.8rem', fontWeight: 'bold', color: '#333', padding: '12px 8px', textAlign: 'center' }}>
                    Agent <span style={{ color: '#BBB', fontSize: '0.65rem', marginLeft: '4px' }}>▲▼</span>
                  </th>
                  <th style={{ fontSize: '0.8rem', fontWeight: 'bold', color: '#333', padding: '12px 8px', textAlign: 'center' }}>
                    Prime <span style={{ color: '#BBB', fontSize: '0.65rem', marginLeft: '4px' }}>▲▼</span>
                  </th>
                  <th style={{ fontSize: '0.8rem', fontWeight: 'bold', color: '#333', padding: '12px 8px', textAlign: 'center' }}>
                    Suc / adr <span style={{ color: '#BBB', fontSize: '0.65rem', marginLeft: '4px' }}>▲▼</span>
                  </th>
                  <th style={{ fontSize: '0.8rem', fontWeight: 'bold', color: '#333', padding: '12px 8px', textAlign: 'center' }}>
                    Sup <span style={{ color: '#BBB', fontSize: '0.65rem', marginLeft: '4px' }}>▲▼</span>
                  </th>
                  <th style={{ fontSize: '0.8rem', fontWeight: 'bold', color: '#333', padding: '12px 8px', textAlign: 'center' }}>
                    Device Id <span style={{ color: '#BBB', fontSize: '0.65rem', marginLeft: '4px' }}>▲▼</span>
                  </th>
                  <th style={{ fontSize: '0.8rem', fontWeight: 'bold', color: '#333', padding: '12px 8px', textAlign: 'center' }}>
                    %Agent <span style={{ color: '#BBB', fontSize: '0.65rem', marginLeft: '4px' }}>▲▼</span>
                  </th>
                  <th style={{ fontSize: '0.8rem', fontWeight: 'bold', color: '#333', padding: '12px 8px', textAlign: 'center' }}>
                    %Sup <span style={{ color: '#BBB', fontSize: '0.65rem', marginLeft: '4px' }}>▲▼</span>
                  </th>
                  <th style={{ fontSize: '0.8rem', fontWeight: 'bold', color: '#333', padding: '12px 8px', textAlign: 'center' }}>
                    Statut <span style={{ color: '#BBB', fontSize: '0.65rem', marginLeft: '4px' }}>▲▼</span>
                  </th>
                  <th style={{ fontSize: '0.8rem', fontWeight: 'bold', color: '#333', padding: '12px 8px', textAlign: 'center' }}>
                    Message <span style={{ color: '#BBB', fontSize: '0.65rem', marginLeft: '4px' }}>▲▼</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((agent, idx) => (
                  <tr key={idx} style={{ verticalAlign: 'middle' }}>
                    {/* Action dropdown button */}
                    <td style={{ padding: '8px', textAlign: 'center' }}>
                      <Dropdown as={ButtonGroup}>
                        <Button
                          style={{
                            backgroundColor: '#007BFF',
                            borderColor: '#007BFF',
                            color: '#FFF',
                            height: '34px',
                            borderRadius: '2px 0 0 2px',
                            fontSize: '0.82rem',
                            fontWeight: '500',
                            padding: '4px 14px'
                          }}
                          onClick={() => { setSelectedAgent(agent); setViewMode("profile"); }}
                        >
                          Action
                        </Button>
                        <Dropdown.Toggle
                          split
                          style={{
                            backgroundColor: '#0069D9',
                            borderColor: '#0069D9',
                            color: '#FFF',
                            height: '34px',
                            borderRadius: '0 2px 2px 0',
                            padding: '4px 8px',
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          <FontAwesomeIcon icon={faCaretDown} style={{ fontSize: '0.8rem' }} />
                        </Dropdown.Toggle>
                        <Dropdown.Menu style={{ borderRadius: '4px', border: '1px solid #DDD', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
                          <Dropdown.Item 
                            style={{ padding: '8px 20px', fontSize: '0.9rem', color: '#333' }}
                            onClick={() => { setSelectedAgent(agent); setViewMode("profile"); }}
                          >
                            Profil
                          </Dropdown.Item>
                          <Dropdown.Item 
                            style={{ padding: '8px 20px', fontSize: '0.9rem', color: '#333' }}
                            onClick={() => handleToggleStatus(agent)}
                          >
                            {agent.is_active ? "Desactivé" : "Activé"}
                          </Dropdown.Item>
                        </Dropdown.Menu>
                      </Dropdown>
                    </td>
                    
                    {/* Agent (Multi-line representation) */}
                    <td style={{ fontSize: '0.82rem', color: '#333', padding: '8px', textAlign: 'center', lineHeight: 1.2 }}>
                      {agent.username} <br />
                      {agent.first_name && <span>{agent.first_name} <br /></span>}
                      {agent.last_name && <span>{agent.last_name}</span>}
                    </td>

                    {/* Prime */}
                    <td style={{ fontSize: '0.85rem', color: '#333', padding: '8px', textAlign: 'center' }}>
                      {agent.prime}
                    </td>

                    {/* Suc / adr */}
                    <td style={{ fontSize: '0.82rem', color: '#333', padding: '8px', textAlign: 'center' }}>
                      {agent.branch_name} <br />
                      <span className="text-muted small">{agent.bank_name}</span>
                    </td>

                    {/* Sup */}
                    <td style={{ fontSize: '0.82rem', color: '#333', padding: '8px', textAlign: 'center' }}>
                      {agent.supervisor_name}
                    </td>
                    
                    {/* Device Id */}
                    <td style={{ fontSize: '0.82rem', color: '#444', padding: '8px', textAlign: 'center' }}>
                      {agent.device_id}
                    </td>
                    
                    {/* %Agent */}
                    <td style={{ fontSize: '0.85rem', color: '#333', padding: '8px', textAlign: 'center' }}>
                      {agent.commission_rate} %
                    </td>
                    
                    {/* %Sup */}
                    <td style={{ fontSize: '0.85rem', color: '#333', padding: '8px', textAlign: 'center' }}>
                      {agent.pct_sup} %
                    </td>
                    
                    {/* Statut */}
                    <td style={{ fontSize: '0.85rem', color: '#333', padding: '8px', textAlign: 'center' }}>
                      {agent.is_active ? "actif" : "inactif"}
                    </td>
                    
                    {/* Message */}
                    <td style={{ fontSize: '0.8rem', color: '#555', padding: '8px', textAlign: 'left', maxWidth: '180px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {agent.message}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>

          {/* Footer pagination */}
          <div className="p-3 d-flex justify-content-between align-items-center border-top">
            <span className="small text-muted fw-bold">Showing 1 to {filtered.length} of {filtered.length} entries</span>
            <ButtonGroup>
              <Button variant="light" className="border btn-sm text-dark font-weight-bold" style={{ backgroundColor: '#F8F9FA', borderRadius: '2px' }}>Previous</Button>
              <Button variant="light" className="border btn-sm text-dark font-weight-bold px-3" style={{ backgroundColor: '#E0E0E0', borderRadius: '2px' }}>1</Button>
              <Button variant="light" className="border btn-sm text-dark font-weight-bold" style={{ backgroundColor: '#F8F9FA', borderRadius: '2px' }}>Next</Button>
            </ButtonGroup>
          </div>

        </Card>
      ) : (
        /* Agent Profil/Detail View */
        <div>
          <Card className="border-0 shadow-sm mb-4" style={{ borderRadius: '8px' }}>
            <Card.Body className="p-4">
              <div className="d-flex justify-content-between align-items-start flex-wrap mb-3" style={{ gap: '15px' }}>
                <div>
                  <h4 style={{ fontWeight: 'bold', margin: '0 0 8px 0' }}>Nom : {selectedAgent.username} {selectedAgent.first_name}</h4>
                  <div style={{ fontSize: '0.95rem', color: '#555', marginBottom: '4px' }}><strong>Device id :</strong> {selectedAgent.device_id}</div>
                  <div style={{ fontSize: '0.95rem', color: '#555' }}>
                    <strong>Statut :</strong> <span className="text-success fw-bold">{selectedAgent.is_active ? "actif" : "inactif"}</span>
                  </div>
                </div>

                <div>
                  <div style={{ fontSize: '0.95rem', color: '#555', marginBottom: '4px' }}><strong>Succursal :</strong> {selectedAgent.branch_name.toLowerCase()}</div>
                  <div style={{ fontSize: '0.95rem', color: '#555' }}><strong>Superviseur :</strong> {selectedAgent.supervisor_name.toLowerCase()}</div>
                </div>

                <div className="d-flex" style={{ gap: '8px' }}>
                  <Button variant="success" size="sm" className="text-white px-3" style={{ borderRadius: '4px' }}>Limite</Button>
                  <Button variant="warning" size="sm" className="px-3" style={{ borderRadius: '4px' }} onClick={handleOpenEdit}>Modifier</Button>
                </div>
              </div>

              <Button 
                variant="danger" 
                size="sm" 
                className="text-white px-3" 
                style={{ borderRadius: '4px' }}
                onClick={() => { handleToggleStatus(selectedAgent); setViewMode("list-active"); }}
              >
                Desactivé pos
              </Button>
            </Card.Body>
          </Card>

          <Row>
            {/* Tirage actif */}
            <Col xs={12} md={3} className="mb-4">
              <Card className="border-0 shadow-sm h-100" style={{ borderRadius: '8px' }}>
                <Card.Header className="bg-white border-0 pt-3 pb-0">
                  <h5 style={{ fontWeight: 'bold', margin: 0 }}>Tirage actif</h5>
                </Card.Header>
                <Card.Body className="pt-2" style={{ maxHeight: '350px', overflowY: 'auto' }}>
                  <div className="mb-3">
                    <span className="small text-muted fw-bold d-block mb-2">Tirages autorisés :</span>
                    <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                      {draws.filter(d => !disabledDraws.some(dd => dd.draw === d.id)).map((d, idx) => (
                        <li key={idx} className="d-flex justify-content-between align-items-center py-1 border-bottom" style={{ fontSize: '0.88rem', color: '#444' }}>
                          <span>{d.name}</span>
                          <Button 
                            variant="link" 
                            className="text-danger p-0 ms-2 fw-bold" 
                            style={{ fontSize: '0.8rem', textDecoration: 'none' }}
                            onClick={() => handleDisableDraw(d.id)}
                          >
                            Exclure
                          </Button>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {disabledDraws.length > 0 && (
                    <div>
                      <span className="small text-danger fw-bold d-block mb-2">Tirages désactivés :</span>
                      <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                        {disabledDraws.map((dd, idx) => (
                          <li key={idx} className="d-flex justify-content-between align-items-center py-1 border-bottom text-muted" style={{ fontSize: '0.88rem', textDecoration: 'line-through' }}>
                            <span>{dd.draw_name || `Tirage #${dd.draw}`}</span>
                            <Button 
                              variant="link" 
                              className="text-success p-0 ms-2 fw-bold" 
                              style={{ fontSize: '0.8rem', textDecoration: 'none' }}
                              onClick={() => handleEnableDraw(dd.id)}
                            >
                              Réactiver
                            </Button>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </Card.Body>
              </Card>
            </Col>

            {/* Boule bloquer */}
            <Col xs={12} md={4} className="mb-4">
              <Card className="border-0 shadow-sm h-100" style={{ borderRadius: '8px' }}>
                <Card.Header className="bg-white border-0 pt-3 pb-0">
                  <h5 style={{ fontWeight: 'bold', margin: 0 }}>Boule bloquer</h5>
                </Card.Header>
                <Card.Body className="pt-2">
                  {blockAlert && (
                    <Alert variant="success" className="py-2 px-3 small mb-3">
                      Ajouter avec success
                    </Alert>
                  )}
                  <div className="d-flex mb-3" style={{ gap: '8px' }}>
                    <Form.Control 
                      type="text" 
                      placeholder="Boule"
                      style={{ borderRadius: '4px', borderColor: '#CCC' }}
                      value={newBlockedNumber}
                      onChange={(e) => setNewBlockedNumber(e.target.value)}
                    />
                    <Button variant="primary" className="text-white px-3" onClick={handleAddBlocked} style={{ borderRadius: '4px' }}>Ajouter</Button>
                  </div>

                   <Table responsive borderless className="mb-0">
                    <tbody>
                      {blockedNumbers.map((num, idx) => (
                        <tr key={idx} style={{ borderBottom: '1px solid #EDEDED' }}>
                          <td style={{ fontSize: '0.95rem', fontWeight: 'bold', color: '#333', padding: '8px 0' }}>
                            {num.number} <span className="badge bg-soft-primary text-primary ms-2" style={{ fontSize: '0.68rem' }}>{num.game_type}</span>
                          </td>
                          <td className="text-end" style={{ padding: '8px 0' }}>
                            <Button 
                              variant="link" 
                              className="text-danger p-0 fw-bold" 
                              style={{ textDecoration: 'none', fontSize: '0.85rem' }}
                              onClick={() => handleRemoveBlocked(num.id)}
                            >
                              Supprimer
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </Card.Body>
              </Card>
            </Col>

            {/* Prime */}
            <Col xs={12} md={5} className="mb-4">
              <Card className="border-0 shadow-sm h-100" style={{ borderRadius: '8px' }}>
                <Card.Header className="bg-white border-0 pt-3 pb-0">
                  <div className="d-flex justify-content-between align-items-center flex-wrap mb-2" style={{ gap: '10px' }}>
                    <h5 style={{ fontWeight: 'bold', margin: 0 }}>Prime</h5>
                    <ButtonGroup size="sm">
                      <Button 
                        variant={primeMode === "general" ? "primary" : "outline-primary"}
                        className={primeMode === "general" ? "text-white px-3" : "px-3"}
                        onClick={() => setPrimeMode("general")}
                      >
                        Général
                      </Button>
                      <Button 
                        variant={primeMode === "par-tirage" ? "success" : "outline-success"}
                        className={primeMode === "par-tirage" ? "text-white px-3" : "px-3"}
                        onClick={() => setPrimeMode("par-tirage")}
                      >
                        Par Tirage
                      </Button>
                      <Button 
                        variant="danger" 
                        className="text-white px-3"
                        onClick={handleResetPrime}
                      >
                        Reset
                      </Button>
                    </ButtonGroup>
                  </div>
                  {primeMode === "par-tirage" && (
                    <div className="d-flex align-items-center mt-2" style={{ gap: '8px' }}>
                      <span className="small text-muted fw-bold">Tirage:</span>
                      <Form.Select 
                        size="sm" 
                        style={{ borderRadius: '4px', border: '1px solid #CCC' }}
                        value={selectedDrawId}
                        onChange={(e) => setSelectedDrawId(e.target.value)}
                      >
                        {draws.map(d => (
                          <option key={d.id} value={d.id}>{d.name}</option>
                        ))}
                      </Form.Select>
                    </div>
                  )}
                </Card.Header>
                <Card.Body className="pt-3">
                  {loadingAgentPrimes ? (
                    <div className="text-center py-4">
                      <Spinner animation="border" size="sm" variant="primary" />
                      <div className="text-muted small mt-2">Chargement des primes de l'agent...</div>
                    </div>
                  ) : (
                    <div>
                      <Table responsive borderless>
                        <thead>
                          <tr style={{ borderBottom: '1px solid #EDEDED' }}>
                            <th style={{ fontSize: '0.8rem', color: '#555', paddingBottom: '8px' }}>Jeux</th>
                            <th style={{ fontSize: '0.8rem', color: '#555', paddingBottom: '8px' }}>Saisie Prime (Surcharge Agent)</th>
                          </tr>
                        </thead>
                        <tbody>
                          {/* Borlette */}
                          <tr>
                            <td style={{ fontSize: '0.9rem', fontWeight: '500', verticalAlign: 'middle', padding: '8px 0' }}>Borlette</td>
                            <td style={{ padding: '8px 0' }}>
                              <div className="d-flex align-items-center" style={{ gap: '6px' }}>
                                <Form.Control 
                                  type="text" 
                                  placeholder="1er lot"
                                  size="sm"
                                  style={{ borderColor: '#28A745', borderRadius: '4px', textAlign: 'center' }}
                                  value={b1}
                                  onChange={(e) => setB1(e.target.value.replace(/[^0-9]/g, ''))}
                                />
                                <Form.Control 
                                  type="text" 
                                  placeholder="2e lot"
                                  size="sm"
                                  style={{ borderColor: '#28A745', borderRadius: '4px', textAlign: 'center' }}
                                  value={b2}
                                  onChange={(e) => setB2(e.target.value.replace(/[^0-9]/g, ''))}
                                />
                                <Form.Control 
                                  type="text" 
                                  placeholder="3e lot"
                                  size="sm"
                                  style={{ borderColor: '#28A745', borderRadius: '4px', textAlign: 'center' }}
                                  value={b3}
                                  onChange={(e) => setB3(e.target.value.replace(/[^0-9]/g, ''))}
                                />
                              </div>
                            </td>
                          </tr>
                          {/* Loto 3 */}
                          <tr>
                            <td style={{ fontSize: '0.9rem', fontWeight: '500', verticalAlign: 'middle', padding: '8px 0' }}>Loto 3</td>
                            <td style={{ padding: '8px 0' }}>
                              <Form.Control 
                                type="text" 
                                size="sm"
                                style={{ borderColor: '#28A745', borderRadius: '4px' }}
                                value={l3}
                                onChange={(e) => setL3(e.target.value.replace(/[^0-9]/g, ''))}
                              />
                            </td>
                          </tr>
                          {/* Mariage */}
                          <tr>
                            <td style={{ fontSize: '0.9rem', fontWeight: '500', verticalAlign: 'middle', padding: '8px 0' }}>Mariage</td>
                            <td style={{ padding: '8px 0' }}>
                              <Form.Control 
                                type="text" 
                                size="sm"
                                style={{ borderColor: '#28A745', borderRadius: '4px' }}
                                value={mar}
                                onChange={(e) => setMar(e.target.value.replace(/[^0-9]/g, ''))}
                              />
                            </td>
                          </tr>
                          {/* Loto 4 */}
                          <tr>
                            <td style={{ fontSize: '0.9rem', fontWeight: '500', verticalAlign: 'middle', padding: '8px 0' }}>Loto 4</td>
                            <td style={{ padding: '8px 0' }}>
                              <div className="d-flex align-items-center" style={{ gap: '6px' }}>
                                <Form.Control 
                                  type="text" 
                                  placeholder="Opt 1"
                                  size="sm"
                                  style={{ borderColor: '#28A745', borderRadius: '4px', textAlign: 'center' }}
                                  value={l4_1}
                                  onChange={(e) => setL4_1(e.target.value.replace(/[^0-9]/g, ''))}
                                />
                                <Form.Control 
                                  type="text" 
                                  placeholder="Opt 2"
                                  size="sm"
                                  style={{ borderColor: '#28A745', borderRadius: '4px', textAlign: 'center' }}
                                  value={l4_2}
                                  onChange={(e) => setL4_2(e.target.value.replace(/[^0-9]/g, ''))}
                                />
                                <Form.Control 
                                  type="text" 
                                  placeholder="Opt 3"
                                  size="sm"
                                  style={{ borderColor: '#28A745', borderRadius: '4px', textAlign: 'center' }}
                                  value={l4_3}
                                  onChange={(e) => setL4_3(e.target.value.replace(/[^0-9]/g, ''))}
                                />
                              </div>
                            </td>
                          </tr>
                          {/* Loto 5 */}
                          <tr>
                            <td style={{ fontSize: '0.9rem', fontWeight: '500', verticalAlign: 'middle', padding: '8px 0' }}>Loto 5</td>
                            <td style={{ padding: '8px 0' }}>
                              <div className="d-flex align-items-center" style={{ gap: '6px' }}>
                                <Form.Control 
                                  type="text" 
                                  placeholder="Opt 1"
                                  size="sm"
                                  style={{ borderColor: '#28A745', borderRadius: '4px', textAlign: 'center' }}
                                  value={l5_1}
                                  onChange={(e) => setL5_1(e.target.value.replace(/[^0-9]/g, ''))}
                                />
                                <Form.Control 
                                  type="text" 
                                  placeholder="Opt 2"
                                  size="sm"
                                  style={{ borderColor: '#28A745', borderRadius: '4px', textAlign: 'center' }}
                                  value={l5_2}
                                  onChange={(e) => setL5_2(e.target.value.replace(/[^0-9]/g, ''))}
                                />
                                <Form.Control 
                                  type="text" 
                                  placeholder="Opt 3"
                                  size="sm"
                                  style={{ borderColor: '#28A745', borderRadius: '4px', textAlign: 'center' }}
                                  value={l5_3}
                                  onChange={(e) => setL5_3(e.target.value.replace(/[^0-9]/g, ''))}
                                />
                              </div>
                            </td>
                          </tr>
                        </tbody>
                      </Table>
                      <div className="text-end mt-3">
                        <Button 
                          variant="success" 
                          size="sm" 
                          className="text-white px-4" 
                          style={{ borderRadius: '4px', fontWeight: 'bold' }}
                          onClick={handleSavePrime}
                        >
                          Enregistrer Prime
                        </Button>
                      </div>
                    </div>
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </div>
      )}

      {/* Modal de modification d'agent */}
      <Modal as={Modal.Dialog} centered show={showEditModal} onHide={() => setShowEditModal(false)}>
        <Modal.Header>
          <Modal.Title className="h6">Modifier l'agent {selectedAgent?.username}</Modal.Title>
          <Button variant="close" aria-label="Close" onClick={() => setShowEditModal(false)} />
        </Modal.Header>
        <Form onSubmit={handleSaveEdit}>
          <Modal.Body>
            {editError && (
              <Alert variant="danger" className="py-2 px-3 small mb-3">
                {editError}
              </Alert>
            )}
            
            <Form.Group className="mb-3">
              <Form.Label className="fw-bold small">Nom d'utilisateur / Identifiant</Form.Label>
              <Form.Control
                required
                type="text"
                value={editForm.username}
                onChange={(e) => setEditForm({ ...editForm, username: e.target.value })}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label className="fw-bold small">Prénom agent</Form.Label>
              <Form.Control
                type="text"
                value={editForm.first_name}
                onChange={(e) => setEditForm({ ...editForm, first_name: e.target.value })}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label className="fw-bold small">Nom agent</Form.Label>
              <Form.Control
                type="text"
                value={editForm.last_name}
                onChange={(e) => setEditForm({ ...editForm, last_name: e.target.value })}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label className="fw-bold small">Succursale</Form.Label>
              <Form.Select
                value={editForm.branch || ""}
                onChange={(e) => setEditForm({ ...editForm, branch: e.target.value })}
              >
                <option value="">Sélectionner une succursale...</option>
                {branches.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label className="fw-bold small">Device ID / IMEI</Form.Label>
              <Form.Control
                type="text"
                value={editForm.device_id}
                onChange={(e) => setEditForm({ ...editForm, device_id: e.target.value })}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label className="fw-bold small">% Agent (Commission)</Form.Label>
              <Form.Control
                type="text"
                value={editForm.commission_rate}
                onChange={(e) => setEditForm({ ...editForm, commission_rate: e.target.value })}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label className="fw-bold small">% Superviseur (Commission)</Form.Label>
              <Form.Control
                type="text"
                value={editForm.supervisor_commission_rate}
                onChange={(e) => setEditForm({ ...editForm, supervisor_commission_rate: e.target.value })}
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowEditModal(false)}>
              Annuler
            </Button>
            <Button variant="primary" type="submit" disabled={savingEdit}>
              {savingEdit ? <Spinner animation="border" size="sm" /> : "Enregistrer"}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
};
